/*
 * Court display modes and the overlay rules that depend on them.
 *
 * Kept free of React/canvas imports (heatmap.js touches `window` at import) so
 * the overlay rules can be unit-tested in the node test environment.
 *
 *  - overview      → average-position avatar markers (no overlay)
 *  - movement      → the focus player's movement heatmap (own half)
 *  - shots         → the focus player's shot landings (opponent half)
 *  - combined      → movement heatmap AND shots together (Visão Geral "map")
 *  - zones/sides/frontback → occupancy divisions (no heat/shot overlay)
 *  - shotLateral/shotDepth → shot zone-analysis: a clickable origin grid on the
 *      player's own half + where shots from the picked zone landed (arrows on
 *      the opponent half). Lateral = left/middle/right, depth = short/med/long.
 */
export type CourtMode =
  | 'overview'
  | 'shots'
  | 'movement'
  | 'combined'
  | 'zones'
  | 'sides'
  | 'frontback'
  | 'shotLateral'
  | 'shotDepth';

/** Whether this mode draws the shot zone-analysis overlay (origin grid +
    destination arrows). Drawn instead of the raw landing markers. */
export const courtShowsShotZones = (mode: CourtMode): boolean =>
  mode === 'shotLateral' || mode === 'shotDepth';

/** Whether this mode paints the shot-landing markers. */
export const courtShowsShots = (mode: CourtMode): boolean =>
  mode === 'shots' || mode === 'combined';

/** Whether this mode paints the movement heatmap layer. */
export const courtShowsHeat = (mode: CourtMode): boolean =>
  mode === 'movement' || mode === 'combined';

/**
 * Court mode for the Visão Geral: the focus player's own map, combining
 * movement heat + shot landings.
 *
 * Shots are advanced-only. Basic reports carry no shot data (the report hides
 * shots everywhere in basic mode), so the map degrades to a movement-only
 * heatmap rather than a `combined` mode that would try to paint shots.
 */
export const overviewCourtMode = (isAdvanced: boolean): CourtMode =>
  isAdvanced ? 'combined' : 'movement';
